import { useState } from 'react';
import { useChatStore, type ChatChannel } from '@/state/chatStore';
import { chat } from '@/systems/ChatSystem';

const CHANNEL_COLOR: Record<ChatChannel, string> = {
  world: '#ffd166', guild: '#81c784', party: '#90caf9', nearby: '#e0e0e0', system: '#ef9a9a',
};

const CHANNEL_LABEL: Record<ChatChannel, string> = {
  world: 'World', guild: 'Guild', party: 'Party', nearby: 'Nearby', system: 'System',
};

const CHANNEL_GLYPH: Record<ChatChannel, string> = {
  world: '◈', guild: '✦', party: '✧', nearby: '◆', system: '⚠',
};

const muStyles = {
  wrapper: {
    position: 'absolute',
    left: 12,
    bottom: 16,
    width: 420,
    padding: 3,
    background: 'linear-gradient(180deg, #d9b673 0%, #7a5a26 50%, #d9b673 100%)',
    border: '1px solid #2a1a08',
    borderRadius: 2,
    boxShadow: '0 0 0 1px #2a1a08, 0 6px 18px rgba(0,0,0,0.7), inset 0 0 0 1px #2a1a08',
    fontFamily: '"Philosopher", "Trajan Pro", "Times New Roman", serif',
  } as React.CSSProperties,
  inner: {
    background: 'linear-gradient(180deg, rgba(8,6,4,0.95) 0%, rgba(20,12,4,0.92) 100%)',
    border: '1px solid #c8a04a',
    padding: 6,
  } as React.CSSProperties,
  tabRow: {
    display: 'flex',
    gap: 2,
    marginBottom: 6,
    borderBottom: '1px solid #6a4d1c',
    paddingBottom: 4,
  } as React.CSSProperties,
  tab: (active: boolean): React.CSSProperties => ({
    flex: 1,
    padding: '4px 6px',
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: 1,
    textTransform: 'uppercase',
    cursor: 'pointer',
    color: active ? '#1a0f04' : '#d9b673',
    background: active
      ? 'linear-gradient(180deg, #f4d27a 0%, #c8993a 50%, #8a6418 100%)'
      : 'linear-gradient(180deg, #2a1d0c 0%, #110a04 100%)',
    border: active ? '1px solid #ffe9a8' : '1px solid #5a3f18',
    borderBottom: active ? '1px solid #6a4318' : '1px solid #3a2810',
    textShadow: active ? '0 1px 0 rgba(255,230,170,0.5)' : '0 1px 0 #000',
    boxShadow: active
      ? 'inset 0 1px 0 rgba(255,255,255,0.4), 0 0 6px rgba(255,209,102,0.4)'
      : 'inset 0 1px 0 rgba(255,255,255,0.05)',
    fontFamily: 'inherit',
  }),
  log: {
    height: 110,
    overflow: 'auto',
    background:
      'linear-gradient(180deg, #050302 0%, #0a0604 100%)',
    border: '1px solid #5a3f18',
    boxShadow: 'inset 0 0 8px rgba(0,0,0,0.9), inset 0 0 0 1px #1a1108',
    padding: '6px 8px',
    fontSize: 12,
    lineHeight: '17px',
    color: '#e6d8b5',
    fontFamily: '"Segoe UI", "Tahoma", sans-serif',
  } as React.CSSProperties,
  msgRow: {
    marginBottom: 2,
    textShadow: '0 1px 0 rgba(0,0,0,0.9)',
  } as React.CSSProperties,
  channelTag: (color: string): React.CSSProperties => ({
    color,
    fontWeight: 700,
    marginRight: 4,
    textShadow: `0 0 4px ${color}66, 0 1px 0 #000`,
  }),
  fromName: {
    color: '#ffd166',
    fontWeight: 700,
    textShadow: '0 0 4px rgba(255,209,102,0.4), 0 1px 0 #000',
  } as React.CSSProperties,
  form: {
    display: 'flex',
    gap: 4,
    marginTop: 6,
  } as React.CSSProperties,
  input: {
    flex: 1,
    background: 'linear-gradient(180deg, #0a0604 0%, #1a1108 100%)',
    color: '#ffe9b0',
    border: '1px solid #5a3f18',
    boxShadow: 'inset 0 0 6px rgba(0,0,0,0.8)',
    padding: '5px 8px',
    fontSize: 12,
    outline: 'none',
    fontFamily: '"Segoe UI", "Tahoma", sans-serif',
  } as React.CSSProperties,
  sendBtn: {
    padding: '4px 14px',
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: 1.5,
    textTransform: 'uppercase' as const,
    color: '#1a0f04',
    background: 'linear-gradient(180deg, #f4d27a 0%, #c8993a 50%, #8a6418 100%)',
    border: '1px solid #ffe9a8',
    borderBottom: '1px solid #6a4318',
    cursor: 'pointer',
    textShadow: '0 1px 0 rgba(255,230,170,0.5)',
    boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.4), 0 0 4px rgba(255,209,102,0.5)',
    fontFamily: 'inherit',
  } as React.CSSProperties,
};

export function Chat() {
  const messages = useChatStore((s) => s.messages);
  const active = useChatStore((s) => s.active);
  const setActive = useChatStore((s) => s.setActive);
  const [text, setText] = useState('');

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!text.trim() || active === 'system') return;
    chat.send(active, text.trim()).catch(() => { /* toast handled */ });
    setText('');
  };

  return (
    <div style={muStyles.wrapper}>
      <div style={muStyles.inner}>
        <div style={muStyles.tabRow}>
          {(['world', 'guild', 'party', 'nearby'] as ChatChannel[]).map((c) => (
            <button
              key={c}
              style={muStyles.tab(c === active)}
              onClick={() => setActive(c)}
              type="button"
            >
              {CHANNEL_LABEL[c]}
            </button>
          ))}
        </div>
        <div style={muStyles.log}>
          {messages
            .filter((m) => m.channel === active || m.channel === 'system')
            .slice(-50)
            .map((m) => (
              <div key={m.id} style={muStyles.msgRow}>
                <span style={muStyles.channelTag(CHANNEL_COLOR[m.channel])}>
                  {CHANNEL_GLYPH[m.channel]} [{CHANNEL_LABEL[m.channel]}]
                </span>
                <span style={muStyles.fromName}>{m.from}:</span>{' '}
                <span>{m.text}</span>
              </div>
            ))}
        </div>
        <form onSubmit={submit} style={muStyles.form}>
          <input
            style={muStyles.input}
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Nhập tin..."
            maxLength={200}
          />
          <button style={muStyles.sendBtn} type="submit">Gửi</button>
        </form>
      </div>
    </div>
  );
}
